import React from "react";
import SEO from "@/components/SEO";
import { Mail, Phone, MapPin, MessageCircle, Clock } from "lucide-react";
import InquiryForm from "@/components/InquiryForm";
import { BRAND } from "@/data/content";
import { trackEvent, EVENTS } from "@/utils/tracking";
import { getWhatsAppLink } from "@/utils/links";
import { buildFAQSchema, SITE_FAQS } from "@/utils/schema";

export default function Contact() {
  const faqLd = buildFAQSchema(SITE_FAQS);
  return (
    <div data-testid="page-contact" className="bg-[#FAF6F1]">
      <SEO
        title="Contact Petals & Promises — Wedding & Event Planners in Gurgaon, Chandigarh"
        description="Begin your celebration with our planning team in Gurgaon (Sector 51) and Chandigarh (Sector 74). Share a few details and we'll respond within one business day."
        path="/contact"
        keywords="contact wedding planner Gurgaon, contact event planner Chandigarh, wedding planner phone number, event management consultation, Petals & Promises contact"
        jsonLd={faqLd}
      />
      <section className="pt-28 md:pt-32 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="pill">Begin Your Event</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D]">
              Let's start a <span className="italic text-[#B65980]">conversation</span>.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-[#36454F]/85 leading-relaxed">
              Share a few details and our planning team will respond within one business day with a curated next step,
              relevant references, and a sensible budget conversation.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-5">
            <div className="lux-card p-7">
              <p className="text-[11px] tracking-[0.24em] uppercase text-[#C5A059]">Studio</p>
              <h3 className="font-display text-2xl mt-2 text-[#3B312D]">Petals & Promises</h3>
              <ul className="mt-5 space-y-4 text-[14.5px] text-[#3B312D]">
                <li className="flex items-start gap-3">
                  <Mail size={16} className="text-[#C5A059] mt-1"/>
                  <a href={`mailto:${BRAND.email}`} className="link-gold" data-testid="contact-email" onClick={() => trackEvent(EVENTS.EMAIL_CLICK, { location: "contact-page" })}>
                    {BRAND.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="text-[#C5A059] mt-1"/>
                  <div className="flex flex-col gap-1">
                    <a href={`tel:${BRAND.phoneTel}`} className="link-gold" data-testid="contact-phone" onClick={() => trackEvent(EVENTS.PHONE_CLICK, { location: "contact-page", value: BRAND.phoneTel })}>{BRAND.phone}</a>
                    <a href={`tel:${BRAND.phoneAltTel}`} className="link-gold" data-testid="contact-phone-alt" onClick={() => trackEvent(EVENTS.PHONE_CLICK, { location: "contact-page-alt", value: BRAND.phoneAltTel })}>{BRAND.phoneAlt}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3"><MapPin size={16} className="text-[#C5A059] mt-1"/>
                  <div>
                    <p>Sector 51, Gurgaon</p>
                    <p className="mt-1">Sector 74, Chandigarh</p>
                    <p className="mt-1">Indira Nagar, Bangalore</p>
                  </div>
                </li>
                <li className="flex items-start gap-3"><Clock size={16} className="text-[#C5A059] mt-1"/><span>Mon – Sat · 10:00 to 19:00</span></li>
              </ul>
              <a href={getWhatsAppLink()} target="_blank" rel="noreferrer noopener" className="btn-gold mt-7" data-testid="contact-whatsapp" onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { location: "contact-page" })}>
                <MessageCircle size={14}/> Chat on WhatsApp
              </a>
            </div>

            <div className="lux-card p-7">
              <p className="text-[11px] tracking-[0.24em] uppercase text-[#C5A059]">Cities we serve</p>
              <p className="mt-3 text-[14px] text-[#36454F]/85 leading-relaxed">
                Gurgaon · Delhi NCR · Noida · Chandigarh · Mohali · Panchkula · Jaipur · Udaipur · Alwar · Jodhpur · Goa ·
                Mumbai · Pune · Bangalore · Hyderabad · Ludhiana · Amritsar · Shimla · Dehradun · Lucknow · Agra ·
                Destination weddings across India and abroad.
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <InquiryForm source="contact-page" />
          </div>
        </div>
      </section>

      {/* FAQs (also injected as schema above for rich results) */}
      <section className="pb-24 md:pb-32 bg-[#F4DCD8]/30 border-t border-[rgba(59,49,45,0.06)]" data-testid="contact-faq-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-20 md:pt-24">
          <p className="pill">FAQs</p>
          <h2 className="font-display text-3xl md:text-5xl mt-5 text-[#3B312D] leading-[1.05]">
            Helpful answers to <span className="italic text-[#B65980]">common questions</span>.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {SITE_FAQS.map((f, i) => (
              <details key={i} className="lux-card p-6 group" data-testid={`contact-faq-${i}`}>
                <summary className="font-display text-xl text-[#3B312D] cursor-pointer marker:hidden list-none flex justify-between items-start gap-3">
                  <span>{f.question}</span>
                  <span className="text-[#B65980] font-light text-2xl leading-none transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-[14.5px] text-[#3B312D]/80 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
